import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { CurrencyCode, Money } from '@vendorhub/shared/domain/catalog';

export type VhPriceSize = 'sm' | 'md' | 'lg';

const DEFAULT_LOCALE_BY_CURRENCY: Record<CurrencyCode, string> = {
  INR: 'en-IN',
  USD: 'en-US',
  EUR: 'en-IE',
  GBP: 'en-GB',
};

/**
 * Renders a currency-formatted price with optional compare-at strikethrough
 * and a percent-off pill. Uses `Intl.NumberFormat` keyed off the currency.
 */
@Component({
  selector: 'vh-price',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="vh-price__amount">{{ amountLabel() }}</span>
    @if (compareLabel()) {
      <span class="vh-price__compare">{{ compareLabel() }}</span>
    }
    @if (discountPct() !== null) {
      <span class="vh-price__off">{{ discountPct() }}% off</span>
    }
  `,
  styleUrl: './price.component.scss',
  host: {
    '[class]': '"vh-price vh-price--" + size()',
  },
})
export class VhPriceComponent {
  readonly price = input.required<Money>();
  readonly compareAtPrice = input<Money | null>(null);
  readonly size = input<VhPriceSize>('md');
  /** Override the auto-picked locale for Intl.NumberFormat. */
  readonly locale = input<string | null>(null);

  readonly amountLabel = computed(() => this.format(this.price()));
  readonly compareLabel = computed(() => {
    const c = this.compareAtPrice();
    return c ? this.format(c) : null;
  });
  readonly discountPct = computed<number | null>(() => {
    const c = this.compareAtPrice();
    const p = this.price();
    if (!c || c.amount <= 0 || c.amount <= p.amount) return null;
    return Math.round(((c.amount - p.amount) / c.amount) * 100);
  });

  private format(money: Money): string {
    const locale = this.locale() ?? DEFAULT_LOCALE_BY_CURRENCY[money.currency] ?? 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: money.currency,
      maximumFractionDigits: Number.isInteger(money.amount) ? 0 : 2,
    }).format(money.amount);
  }
}
